import type { BookableServiceRecord, MoneyRecord, RecallExternalPriceRecord } from '@navio/nsdk';

export type ProposalCatalogPrice = {
  bookingProposalItemId: number;
  salesItemId: number;
  licensePlateNumber: string;
  grossPrice: MoneyRecord;
  originalGrossPrice: MoneyRecord;
  priceListName: string;
  discountPercent: number;
};

type ProposalSalesItemLine = {
  bookingProposalItemId: number;
  salesItemId: number;
  salesItemName: string;
  licensePlateNumber: string;
};

type DiscountedBookable = Pick<
  BookableServiceRecord,
  'salesItemId' | 'name' | 'grossPrice' | 'originalGrossPrice' | 'priceListName' | 'discountPercent'
>;

const normalizeName = (name: string) => name.toLocaleLowerCase().replace(/\s+/g, ' ').trim();

// Mirror apps/recalls/services/recall_external_pricing.py — match by sales
// item id, else by unique normalized name (adapter catalogs reuse names with
// different ids than the proposal's core/import row).
export function matchProposalCatalogPrices({
  lines,
  bookableServices
}: {
  lines: ProposalSalesItemLine[];
  bookableServices: DiscountedBookable[];
}): ProposalCatalogPrice[] {
  const discounted = bookableServices.filter(
    (
      service
    ): service is DiscountedBookable & {
      originalGrossPrice: MoneyRecord;
      priceListName: string;
      discountPercent: number;
    } => service.originalGrossPrice != null && service.priceListName != null && service.discountPercent != null
  );

  const bySalesItemId = new Map(discounted.map((service) => [service.salesItemId, service]));
  const byUniqueName = new Map<string, (typeof discounted)[number]>();
  const duplicateNames = new Set<string>();
  for (const service of discounted) {
    const key = normalizeName(service.name);
    if (byUniqueName.has(key)) {
      duplicateNames.add(key);
      continue;
    }
    byUniqueName.set(key, service);
  }
  for (const name of duplicateNames) {
    byUniqueName.delete(name);
  }

  const prices: ProposalCatalogPrice[] = [];
  for (const line of lines) {
    const service = bySalesItemId.get(line.salesItemId) ?? byUniqueName.get(normalizeName(line.salesItemName));
    if (!service) {
      continue;
    }
    prices.push({
      bookingProposalItemId: line.bookingProposalItemId,
      salesItemId: line.salesItemId,
      licensePlateNumber: line.licensePlateNumber,
      grossPrice: service.grossPrice,
      originalGrossPrice: service.originalGrossPrice,
      priceListName: service.priceListName,
      discountPercent: service.discountPercent
    });
  }
  return prices;
}

export function proposalCatalogPricesFromExternalRecords({
  externalPrices,
  lines
}: {
  externalPrices: RecallExternalPriceRecord[];
  lines: ProposalSalesItemLine[];
}): ProposalCatalogPrice[] {
  const lineByKey = new Map(lines.map((line) => [`${line.bookingProposalItemId}:${line.salesItemId}`, line] as const));
  const prices: ProposalCatalogPrice[] = [];
  for (const price of externalPrices) {
    const line = lineByKey.get(`${price.bookingProposalItemId}:${price.salesItemId}`);
    if (!line) {
      continue;
    }
    prices.push({
      bookingProposalItemId: price.bookingProposalItemId,
      salesItemId: price.salesItemId,
      licensePlateNumber: line.licensePlateNumber,
      grossPrice: price.grossPrice,
      originalGrossPrice: price.originalGrossPrice,
      priceListName: price.priceListName,
      discountPercent: price.discountPercent
    });
  }
  return prices;
}

export function catalogPriceMapKey(licensePlateNumber: string, salesItemId: number) {
  return `${licensePlateNumber}-${salesItemId}`;
}
